import { Linkedin } from "lucide-react";

/**
 * FounderCard — editorial founder tile: portrait on top, name + title + bio below.
 *
 * If `photo` is not provided, renders a monogrammed navy tile with the founder's
 * initials in the display serif on a cream underline — matches the brand system
 * and can be swapped for a real headshot later without changing layout.
 */
export default function FounderCard({ name, title, bio, photo, linkedin, testId }) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
  const normalizedLinkedIn = linkedin
    ? (linkedin.startsWith("http") ? linkedin : `https://${linkedin}`)
    : null;

  return (
    <article
      className="flex flex-col bg-white border border-[var(--line)] hover:border-[var(--gold)] transition-colors group"
      data-testid={testId}
    >
      {/* Portrait */}
      <div className="aspect-[4/5] w-full relative overflow-hidden bg-[var(--navy)]">
        {photo ? (
          <img
            src={photo}
            alt={`Portrait of ${name}`}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <>
            {/* Grain overlay */}
            <div
              className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140' viewBox='0 0 140 140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/%3E%3C/filter%3E%3Crect width='140' height='140' filter='url(%23n)' opacity='0.55'/%3E%3C/svg%3E\")",
              }}
              aria-hidden="true"
            />
            {/* Corner accent */}
            <div className="absolute top-6 left-6 w-10 h-[2px] bg-[var(--gold)]" aria-hidden="true" />
            {/* Monogram */}
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="font-display text-[var(--cream)] text-[110px] leading-none tracking-tight opacity-90 select-none">
                {initials}
              </span>
            </div>
            {/* Bottom slug */}
            <div className="absolute bottom-6 left-6 right-6 flex items-center gap-3">
              <div className="w-8 h-[2px] bg-[var(--gold)]" aria-hidden="true" />
              <span className="text-[10px] tracking-[0.25em] text-[var(--gold)] font-bold">CO-FOUNDER</span>
            </div>
          </>
        )}
      </div>

      {/* Copy */}
      <div className="p-7 flex-1 flex flex-col">
        <h3 className="font-display text-2xl text-[var(--navy)] leading-tight" data-testid={`${testId}-name`}>
          {name}
        </h3>
        <div className="text-[11px] tracking-[0.2em] text-[var(--gold)] font-bold mt-2 uppercase">
          {title}
        </div>
        <p className="text-[var(--muted)] text-sm leading-relaxed mt-4 flex-1">
          {bio}
        </p>
        {normalizedLinkedIn && (
          <a
            href={normalizedLinkedIn}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-xs font-bold text-[var(--navy)] hover:text-[var(--gold)] mt-6 self-start border-b border-transparent hover:border-[var(--gold)] pb-0.5 transition-colors"
            data-testid={`${testId}-linkedin`}
          >
            <Linkedin size={14} /> Connect on LinkedIn
          </a>
        )}
      </div>
    </article>
  );
}
